import { forwardRef, ReactNode } from 'react';
import { cx } from '@linaria/atomic';
import { Icon, IconName } from '@frontend/icons';
import { splitStylesProp, type StylesProp } from '../../helpers';
import { Text } from '../text/text.component';
import { Truncated } from '../truncated';
import * as styles from './inline-notification.styles';

type BaseProps = {
  status: 'success' | 'warn' | 'error' | 'info' | 'premium';
  size?: 'large' | 'medium' | 'small';
  maxWidth?: string;
  customStyles?: StylesProp;
  /** Semantic variant for content styling. 'borderless' removes the content border. */
  hideBorder?: boolean;
};

type PropsMessage = {
  message: ReactNode;
  children?: never;
};

type PropsChildren = {
  message?: never;
  children: ReactNode;
};

type Props = (BaseProps & PropsMessage) | (BaseProps & PropsChildren);

export type InlineNotificationProps = Props & Omit<React.ComponentPropsWithoutRef<'section'>, keyof Props>;

type IconSelectionProps = Record<InlineNotificationProps['status'], IconName>;

const IconSelection: IconSelectionProps = {
  success: 'success-filled',
  warn: 'alert-filled',
  error: 'error-filled',
  info: 'info-filled',
  premium: 'premium-badge',
};

const iconSize = {
  large: 24,
  medium: 24,
  small: 16,
} as const;

export const InlineNotification = forwardRef<HTMLElement, InlineNotificationProps>(
  ({ children, maxWidth, message, size = 'large', status, customStyles, hideBorder = false, ...rest }, ref) => {
    const iconName = IconSelection[status];

    const { css: customCss, className: customClassName } = splitStylesProp(customStyles);

    const containerClass = cx(
      styles.containerBaseStyle,
      styles.containerSizeStyles[size],
      styles.containerStatusStyles[status],
      customClassName
    );

    const contentClass = cx(
      styles.contentBaseStyle,
      styles.contentSizeStyles[size],
      styles.contentStatusStyles[status],
      hideBorder && styles.hideBorderStyle
    );

    return (
      <section
        {...(customCss !== undefined ? { css: customCss } : undefined)}
        className={containerClass}
        style={maxWidth ? { maxWidth } : undefined}
        ref={ref}
        {...rest}
      >
        <div className={contentClass}>
          <Icon size={iconSize[size]} name={iconName} />
          {typeof message === 'string' ? (
            size === 'large' ? (
              <Text style={{ margin: 0 }} size={'medium'}>
                {message}
              </Text>
            ) : (
              <Truncated as={Text} style={{ margin: 0 }} size={size === 'small' ? 'small' : 'medium'}>
                {message}
              </Truncated>
            )
          ) : (
            children
          )}
        </div>
      </section>
    );
  }
);

InlineNotification.displayName = 'InlineNotification';
